import {AbstractStripePayment, type ConfirmResult, type TokenResult} from './abstract/class-abstract-stripe-payment';
import type {StripeIbanElement, Stripe, StripeElements, CreatePaymentMethodSepaDebitData} from '@stripe/stripe-js';
import {$qs} from '../../../@shared/ts/dom';
import {observeCheckoutHTML} from '../checkout';
import {billingDetails as getBillingDetails} from './stripe';
import {createSetupIntent} from './setup-intent';
import {getErrorString} from '../../../@shared/ts/error';
import {type PeachPayCheckoutData} from '../types';

declare const peachpay_checkout_data: PeachPayCheckoutData;

class StripeSepaDebitPayment extends AbstractStripePayment {
	private readonly $iban: StripeIbanElement | undefined;

	constructor(stripe: Stripe, elements: StripeElements) {
		super(stripe, elements);

		const options = {
			style: {
				base: {
					color: '#32325d',
					fontSize: '16px',
				},
			},

			supportedCountries: ['SEPA'],
		};

		this.$iban = this.elements.create('iban', options);

		observeCheckoutHTML(() => {
			this.mountIBAN();
		});

		this.mountIBAN();
	}

	public mountIBAN() {
		const $container = $qs('#pp-stripe-sepadebit-element');
		if (!$container || !this.$iban || $container.querySelector('.pp-stripe-sepadebit-iban-mount')) {
			return;
		}

		// Create mount point.
		const $ibanMount = document.createElement('div');
		$ibanMount.classList.add('pp-stripe-sepadebit-iban-mount');
		$container.prepend($ibanMount);

		try {
			this.$iban.mount('#pp-stripe-sepadebit-element div.pp-stripe-sepadebit-iban-mount');
		} catch (e) {
			console.error(getErrorString(e));
		}
	}

	public gatewayId() {
		return 'peachpay_stripe_sepadebit';
	}

	public async tokenize(): Promise<TokenResult> {
		if (!this.$iban) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'Failed to find IBAN element.',
			}];
		}

		let billingDetails = getBillingDetails() as CreatePaymentMethodSepaDebitData['billing_details'];

		if (!billingDetails.email || billingDetails.name === ' ') {
			if (peachpay_checkout_data.page.is_order_pay && peachpay_checkout_data.order_pay_details) {
				billingDetails = {
					name: `${peachpay_checkout_data.order_pay_details.billing_first_name ?? ''} ${peachpay_checkout_data.order_pay_details.billing_last_name ?? ''}`,
					email: `${peachpay_checkout_data.order_pay_details.billing_email ?? ''}`,
				};
			} else {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Billing name and billing email are required fields.',
				}];
			}
		}

		const {paymentMethod, error} = await this.stripe.createPaymentMethod({
			type: 'sepa_debit',
			sepa_debit: this.$iban,
			billing_details: billingDetails,
		});

		if (error) {
			const message = error.message ?? 'Failed to tokenize payment method.';

			return [null, {
				paymentStatus: 'creationfailed',
				note: message,
			}];
		}

		if (!paymentMethod?.id) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'Failed to tokenize payment method.',
			}];
		}

		let payDetails;
		if (peachpay_checkout_data.page.is_order_pay && peachpay_checkout_data.order_pay_details) {
			payDetails = peachpay_checkout_data.order_pay_details;
		} else {
			payDetails = peachpay_checkout_data.cart_details;
		}

		if (!payDetails.is_only_free_trial) {
			return [paymentMethod.id, {}];
		}

		const clientSecret = await createSetupIntent('sepa_debit');
		if (!clientSecret) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'Failed to create setup intent.',
			}];
		}

		const {error: collectError, setupIntent: collectIntent} = await this.stripe.confirmSepaDebitSetup(
			clientSecret,
			{
				payment_method: {
					sepa_debit: this.$iban,
					billing_details: {
						name: billingDetails.name,
						email: billingDetails.email,
					},
				},
			},
		);

		if (!collectIntent || collectError) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: collectError?.message ?? 'Failed to collect IBAN for setup intent.',
			}];
		}

		if (collectIntent.status !== 'succeeded') {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'Failed to tokenize payment method.',
			}];
		}

		return [collectIntent.payment_method as string, {}];
	}

	public async confirm(clientSecret: string): Promise<ConfirmResult> {
		const {paymentIntent, error} = await this.stripe.retrievePaymentIntent(clientSecret);

		if (!paymentIntent || error) {
			const message = error?.message ?? 'Failed to retrieve payment intent.';
			return [null, {
				note: message,
			}];
		}

		if (paymentIntent.status === 'succeeded'
			|| paymentIntent.status === 'requires_capture'
			|| paymentIntent.status === 'processing') {
			return ['success', {}];
		}

		if (paymentIntent.status === 'requires_action') {
			const {paymentIntent, error} = await this.stripe.confirmSepaDebitPayment(clientSecret);
			if (!paymentIntent || error) {
				const message = error?.message ?? 'Failed to confirm payment intent.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent.status === 'succeeded'
				|| paymentIntent.status === 'requires_capture'
				|| paymentIntent.status === 'processing') {
				return ['success', {}];
			}
		}

		return [null, {
			note: `Failed to confirm payment intent with status(${paymentIntent.status})`,
		}];
	}
}

export {
	StripeSepaDebitPayment,
};
